<!-- @catalog Modal dialog with header/footer slots, for richer overlays. For a simple confirm/prompt use CDialog. -->
<template>
  <q-dialog
    ref="dialogRef"
    class="modal"
    :persistent="persistent || disableClose"
    :no-esc-dismiss="disableClose"
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin tw-flex tw-max-h-[90vh] tw-w-full tw-flex-col !tw-rounded-2xl !tw-shadow-xl"
      :class="{ '!tw-overflow-visible': overflowVisible }"
    >
      <div class="tw-flex tw-items-center tw-gap-4 tw-border-b tw-px-6 tw-py-5">
        <h4
          class="tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-lg tw-font-semibold"
        >
          {{ title }}
        </h4>

        <c-button-icon
          v-if="!disableClose"
          flat
          color="grey-800"
          icon="fas fa-times"
          @click="onDialogCancel"
        />
      </div>

      <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <div
          class="tw-flex-shrink tw-space-y-6 tw-p-6"
          :class="{
            'tw-overflow-auto tw-overflow-y-auto': !overflowVisible,
            '!tw-overflow-visible': overflowVisible,
            'tw-pb-3': !hideActions,
          }"
        >
          <div v-if="message">
            {{ message }}
          </div>

          <div v-if="$slots.default" class="tw-flex tw-flex-col">
            <slot />
          </div>
        </div>

        <div v-if="safeword" class="tw-px-6 tw-pb-3">
          <c-input
            v-model="typedSafeword"
            autofocus
            :label="`Typ '${safeword}' om door te gaan.`"
          />
        </div>

        <div
          v-if="!hideActions"
          class="tw-flex tw-flex-shrink-0 tw-flex-wrap tw-justify-start tw-gap-1 tw-p-6 tw-pt-3 sm:tw-justify-end"
        >
          <slot v-if="hasCustomActions" name="actions" />

          <template v-if="!hasCustomActions">
            <c-button
              v-if="cancel"
              color="transparent"
              text-color="gray"
              label="Annuleren"
              @click="onDialogCancel"
            />

            <!-- The tooltip sits on the wrapper, not the button: a
                 native-disabled button swallows mouse events, so a tooltip
                 inside it would never show -- and explaining a disabled
                 button is exactly what primaryTooltip is for. -->
            <div class="c-modal__primary">
              <c-tooltip v-if="primaryTooltip.length">
                {{ primaryTooltip }}
              </c-tooltip>

              <c-button
                id="primary-button"
                :disable="isPrimaryDisabled"
                :loading="primaryLoading"
                :color="dialogType.color"
                :label="primaryLabel"
                @click="onOkClick"
              />
            </div>
          </template>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useConfetti } from "@/composables/useConfetti";
import { MODAL_SIZES } from "shared/constants";
import { useDialogPluginComponent } from "quasar";
import { computed, onBeforeUnmount, onMounted, ref, useSlots } from "vue";

const props = defineProps({
  title: {
    type: String,
    default: () => "",
  },
  message: {
    type: [String, null],
    default: () => null,
  },
  cancel: {
    type: Boolean,
    default: () => true,
  },
  persistent: {
    type: Boolean,
    default: () => true,
  },
  primaryLabel: {
    type: String,
    default: () => "",
  },
  type: {
    type: String,
  },
  returnData: {
    type: [String, Object, Array],
    default: "",
  },
  size: {
    type: String,
    default: () => "md",
    validator: (value) => Object.keys(MODAL_SIZES).includes(value),
  },
  // Escape hatch: an explicit CSS max-width that overrides `size`. Prefer
  // `size` so widths stay consistent across the app.
  maxWidth: {
    type: String,
    default: () => null,
  },
  disablePrimaryButton: {
    type: Boolean,
    default: () => false,
  },
  primaryLoading: {
    type: Boolean,
    default: () => false,
  },
  // Shown on the primary button; pair it with `disablePrimaryButton` to say
  // why the action is unavailable.
  primaryTooltip: {
    type: String,
    default: () => "",
  },
  disableClose: {
    type: Boolean,
    default: () => false,
  },
  validate: {
    type: Function,
    default: () => () => true,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  overflowVisible: {
    type: Boolean,
    default: () => false,
  },
  confetti: {
    type: Boolean,
    default: false,
  },
  safeword: {
    type: String,
    default: null,
  },
});

// Slots
const slots = useSlots();

defineEmits([...useDialogPluginComponent.emits]);

const resolvedMaxWidth = computed(
  () => props.maxWidth ?? MODAL_SIZES[props.size],
);

const dialogType = computed(() => {
  switch (props.type) {
    case "warning":
      return {
        class: "tw-text-warning",
        icon: "fas fa-exclamation-triangle",
        color: "warning",
      };
    case "negative":
      return {
        class: "tw-text-negative",
        icon: "fas fa-exclamation-circle",
        color: "negative",
      };
    case "success":
      return {
        class: "tw-text-success",
        icon: "fas fa-check-circle",
        color: "positive",
      };
    case "info":
      return {
        class: "tw-text-primary",
        icon: "fas fa-info-circle",
        color: "primary",
      };
    default:
      return {
        color: "primary",
      };
  }
});

const hasCustomActions = computed(() => {
  const { actions } = slots || {};

  return !!actions;
});

const typedSafeword = ref("");

const isSafewordSatisfied = computed(() => {
  if (!props.safeword) {
    return true;
  }

  return typedSafeword.value.toUpperCase() === props.safeword.toUpperCase();
});

const isPrimaryDisabled = computed(
  () => props.disablePrimaryButton || !isSafewordSatisfied.value,
);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

defineExpose({ dialogRef, onDialogOK, onDialogCancel });

const onOkClick = async () => {
  const { validate = () => true } = props || {};

  if (await validate()) {
    const { confetti } = props;

    if (confetti) {
      useConfetti("#primary-button");
    }
    onDialogOK(props.returnData);
  }
};

onMounted(() => {
  // Sentry will use this to check if a modal is open
  document.body.classList.add("overflow-hidden");
});

onBeforeUnmount(() => {
  // Sentry will use this to check if a modal is open
  document.body.classList.remove("overflow-hidden");
});
</script>

<style lang="scss" scoped>
.q-dialog.modal {
  .q-card {
    @apply tw-w-full;
    max-width: v-bind(resolvedMaxWidth);
  }
}

.c-modal__primary {
  @apply tw-inline-flex;

  // Let the disabled button fall through to the wrapper for hit-testing, so
  // the wrapper's tooltip still opens on hover.
  :deep(.q-btn.disabled) {
    pointer-events: none !important;
  }
}
</style>
